"use client";
import Link from 'next/link';
import React, { useEffect, useState } from 'react';

const Breadcrumb: React.FC = () => {
    const baseURL = process.env.NEXT_PUBLIC_BASE_URL || '';
    const [breadcrumbJsonLd, setBreadcrumbJsonLd] = useState<string | null>(null);
    const [pathArray, setPathArray] = useState<string[]>([]);

    useEffect(() => {
        if (typeof window !== "undefined") {
            const pathSegments = window.location.pathname.split('/').filter((segment) => segment !== '');
            setPathArray(pathSegments);

            const generateBreadcrumbs = () => {
                const breadcrumbItems = pathSegments.map((segment, index) => {
                    const url = `${baseURL}/${pathSegments.slice(0, index + 1).join('/')}/`;
                    return {
                        "@type": "ListItem",
                        "position": index + 2,
                        "name": segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
                        "item": url
                    };
                });

                const homeBreadcrumb = {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": baseURL + '/'
                };

                return [homeBreadcrumb, ...breadcrumbItems];
            };

            const breadcrumbListSchema = {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": generateBreadcrumbs()
            };

            setBreadcrumbJsonLd(JSON.stringify(breadcrumbListSchema));
        }
    }, [baseURL]);

    return (
        <div className="items-center text-sm text-blue-600 hide-scrollbar mt-50"
            style={{
                overflowX: 'scroll', whiteSpace: 'nowrap', maxWidth: '100%',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch'
            }}
        >
            <span className="items-center">
                <Link href="/" passHref>
                    <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 576 512">
                        <path d="M575.8 255.5c0 18-15 32.1-32 32.1l-32 0 .7 160.2c0 2.7-.2 5.4-.5 8.1l0 16.2c0 22.1-17.9 40-40 40l-16 0c-1.1 0-2.2 0-3.3-.1c-1.4 .1-2.8 .1-4.2 .1L416 512l-24 0c-22.1 0-40-17.9-40-40l0-24 0-64c0-17.7-14.3-32-32-32l-64 0c-17.7 0-32 14.3-32 32l0 64 0 24c0 22.1-17.9 40-40 40l-24 0-31.9 0c-1.5 0-3-.1-4.5-.2c-1.2 .1-2.4 .2-3.6 .2l-16 0c-22.1 0-40-17.9-40-40l0-112c0-.9 0-1.9 .1-2.8l0-69.7-32 0c-18 0-32-14-32-32.1c0-9 3-17 10-24L266.4 8c7-7 15-8 22-8s15 2 21 7L564.8 231.5c8 7 12 15 11 24z" />
                    </svg>
                </Link>
            </span>

            {pathArray.map((segment, index) => {
                const fullPath = '/' + pathArray.slice(0, index + 1).join('/') + '/';
                return (
                    <span key={index} className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} className="w-4 h-4 text-gray-500 mx-2" viewBox="0 0 320 512">
                            <path fill="currentColor" d="M285.5 273l-192 176c-9.5 8.7-24.1 7.9-33.2-1.7l-13.2-14.3c-8.9-9.7-8.2-24.6 1.7-33.5L210.1 256 48.6 112.5c-9.9-8.9-10.7-23.8-1.7-33.5l13.2-14.3c9.1-9.6 23.7-10.4 33.2-1.7l192 176c4.9 4.5 7.7 10.8 7.7 17.4s-2.8 12.9-7.7 17.4z" />
                        </svg>
                        <b>
                            <Link href={fullPath} className="hover:underline">
                                {segment.replace(/-/g, ' ')}
                            </Link>
                        </b>
                    </span>
                );
            })}

            {breadcrumbJsonLd && (
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbJsonLd }} />
            )}
        </div>
    );
};

export default Breadcrumb;
